import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import type { Clip, ObjectType } from '@/@types/graphql-types';
import { ClipProvider, SraEventAction } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';


export default /*#__PURE__*/_defineComponent({
  __name: 'BackdropTrailer',
  props: {
    titleName: null,
    imageUrl: { default: '' },
    isTrailerPlaying: { type: Boolean },
    isDisneyPlusOffer: { type: Boolean },
    clips: null,
    mute: { type: Boolean },
    source: { default: '' },
    value: null,
    autoplay: { type: Boolean },
    limit: { default: 4 },
    titleObjectType: null,
    titleId: null,
    additionalContexts: { default: () => [] },
    influencedSponsoredAd: null
  },
  emits: ["activate", "ended"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'activate', id: string | null): void;
	(e: 'ended'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titleName?: string, imageUrl: string, isTrailerPlaying?: boolean, isDisneyPlusOffer?: boolean, clips: Clip[], mute?: boolean, source: string, value?: string, autoplay?: boolean, limit: number, titleObjectType?: ObjectType, titleId?: string, additionalContexts: SnowplowContext[], influencedSponsoredAd?: SponsoredAdFragment };

const VideobusterPlayer = () => import('@/ui-components/VideobusterPlayer.vue');
const YoutubePlayer = () => import('@/ui-components/YoutubePlayer.vue');
const DailyMotionPlayer = () => import('@/ui-components/DailyMotionPlayer.vue');
const PlexPlayer = () => import('@/ui-components/PlexPlayer.vue');





const trackingProviders: Partial<Record<ClipProvider | 'PLEX', number>> = {
	[ClipProvider.Youtube]: 1,
	[ClipProvider.Videobuster]: 2,
	[ClipProvider.Dailymotion]: 3,
	PLEX: 4,
};
const trackingMap = {
	buffering: 'youtube_buffering',
	playing: 'youtube_playing',
	paused: 'youtube_paused',
};

const { trackSraEvent } = useBackendTracking();

const isMounted = ref(false);
const hasPlayedClips = ref<Record<string, boolean>>({});

const visibleClips = computed(() => props.clips.slice(0, props.limit));

watch(
	() => props.isTrailerPlaying,
	isPlaying => {
		if (!isPlaying) {
			emit('activate', null);
		}
	}
);

onMounted(() => {
	isMounted.value = true;

	TrackingHelper.trackEvent('clip_spinner', {
		action: 'finished',
		value: 0,
		property: visibleClips.value.length > 0 ? visibleClips.value[0].provider : 'none',
		nonInteraction: true,
	});
});

const playerVars = computed<YoutubePlayerOptions>(() => ({
	autoplay: props.autoplay ? 1 : 0,
	mute: props.mute ? 1 : 0,
}));

const clipsProvider = computed(() => props.clips[0]?.provider as ClipProvider | 'PLEX' | 'JUSTWATCH_TV');

const videoPlayer = computed(() => {
	switch (clipsProvider.value) {
		case ClipProvider.Videobuster:
			return VideobusterPlayer;
		case ClipProvider.Dailymotion:
		case 'JUSTWATCH_TV':
			return DailyMotionPlayer;
		case 'PLEX':
			return PlexPlayer;
		default:
			return YoutubePlayer;
	}
});

function onActivate(clipId: string | null) {
	const id = clipId ? `${props.source}:${clipId}` : null;
	emit('activate', id);
}

function isActiveClip(clip: Clip) {
	const id = props.value?.split(/:(.*)/s);
	if (!id) return false;

	return id[0] === props.source && id[1] == clip.externalId;
}

function onTrack(eventName: 'buffering' | 'playing' | 'paused' | 'ended', clip: Clip) {
	// for TrailerAutoplay module
	if (eventName === 'ended') {
		return emit('ended');
	}

	const category = trackingMap[eventName];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: props.titleObjectType?.toLowerCase(),
				label: props.source,
				nonInteraction: props.autoplay,
				value: trackingProviders[clip.provider],
			},
			props.additionalContexts
		);
	}

	// track youtube_started only once if the clip is played at least once
	if (eventName === 'playing') {
		// track influenced campaign metrics
		const influenced = !!props.influencedSponsoredAd?.bidId;

		if (influenced) {
			trackSraEvent({
				action: SraEventAction.PlayTrailerClicked,
				sponsoredAd: () => props.influencedSponsoredAd,
				jwEntityID: props.titleId,
			});
		}

		if (!(clip.externalId in hasPlayedClips.value)) {
			TrackingHelper.trackEvent(
				'youtube_started',
				{
					action: props.titleObjectType?.toLowerCase(),
					label: props.source,
					value: trackingProviders[clip.provider],
				},
				props.additionalContexts
			);
		}

		hasPlayedClips.value[clip.externalId] = true;
	}
}

return { __sfc: true,VideobusterPlayer, YoutubePlayer, DailyMotionPlayer, PlexPlayer, props, emit, trackingProviders, trackingMap, trackSraEvent, isMounted, hasPlayedClips, visibleClips, playerVars, clipsProvider, videoPlayer, onActivate, isActiveClip, onTrack, PictureComp }
}

})